/**
 * @page /m/dokumente
 * @description Mobile Portal — Dokumente, Lohnzettel, Vertraege.
 *   Optimiert fuer Smartphone-Darstellung mit vertikalen Karten-Sektionen.
 * @features
 *   - Offene Vertraege (Unterschrift-Warnung, prominent)
 *   - Lohnzettel (nach Jahr gruppiert, Download)
 *   - Vertraege (Status-Badge, PDF-Download)
 *   - Persoenliche + Allgemeine Dokumente (Kategorie-Badge, Download)
 * @api GET /api/portal/vertraege, GET /api/portal/lohnzettel
 * @api GET /api/portal/dokumente, GET /api/portal/allgemeine-dokumente
 * @roles portal_mitarbeiter
 * @i18n mobilePortal.dokumente
 * @since v2.243.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { FileText, Download, AlertTriangle, ChevronDown, ChevronRight, Building2, FolderOpen } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMonth(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

function formatEur(val) {
  if (val == null) return '';
  const n = Number(val);
  return n >= 10 ? `€ ${Math.round(n).toLocaleString('de-DE')}` : `€ ${n.toFixed(1)}`;
}

function StatusBadge({ status, t }) {
  const isActive = status === 'aktiv' || status === 'unterschrieben';
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
      isActive
        ? 'bg-[#338edc]/10 text-[#338edc]'
        : 'bg-[var(--bg-inset)] text-[var(--text-tertiary)]'
    }`}>
      {status || t('mobilePortal.dokumente.unknown', 'Unbekannt')}
    </span>
  );
}

function SectionCard({ title, icon: Icon, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl overflow-hidden mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
      >
        {Icon && <Icon className="w-5 h-5 text-[#338edc] flex-shrink-0" />}
        <span className="flex-1 text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{title}</span>
        {count != null && (
          <span className="px-2 py-0.5 rounded-full bg-[var(--bg-inset,#F8F9FC)] text-[10px] font-bold text-[var(--text-tertiary,#6B7280)]">
            {count}
          </span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" /> : <ChevronRight className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />}
      </button>
      {open && (
        <div className="border-t border-[var(--border-card,#E5E7EB)] px-4 pb-3">
          {children}
        </div>
      )}
    </div>
  );
}

export default function MobileDokumente() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [vertraege, setVertraege] = useState([]);
  const [lohnzettel, setLohnzettel] = useState([]);
  const [meineDoks, setMeineDoks] = useState([]);
  const [allgDoks, setAllgDoks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [vRes, lRes, dRes, aRes] = await Promise.all([
        fetch(`${API}/api/portal/vertraege`, { credentials: 'include' }),
        fetch(`${API}/api/portal/lohnzettel`, { credentials: 'include' }),
        fetch(`${API}/api/portal/dokumente`, { credentials: 'include' }),
        fetch(`${API}/api/portal/allgemeine-dokumente`, { credentials: 'include' }),
      ]);
      if (vRes.ok) setVertraege(await vRes.json());
      if (lRes.ok) setLohnzettel(await lRes.json());
      if (dRes.ok) setMeineDoks(await dRes.json());
      if (aRes.ok) setAllgDoks(await aRes.json());
    } catch (err) {
      console.error('MobileDokumente load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // #1150 — Nur digitale Vertraege, die aktiv auf Unterschrift warten (status=gesendet, nicht signiert).
  // Papier-/Odoo-Vertraege (vertragsart='papier') werden extern signiert und brauchen KEINE Portal-Aktion.
  // Entwurf-Vertraege liefert das Backend nicht mehr aus (interne HR-Drafts).
  const offeneVertraege = useMemo(
    () => vertraege.filter(v => v.requires_signature === true),
    [vertraege]
  );

  // Lohnzettel grouped by year
  const lohnzettelByYear = useMemo(() => {
    const groups = {};
    for (const lz of lohnzettel) {
      const year = lz.gueltig_ab ? new Date(lz.gueltig_ab).getFullYear() : 'Sonstige';
      if (!groups[year]) groups[year] = [];
      groups[year].push(lz);
    }
    return Object.entries(groups).sort(([a], [b]) => String(b).localeCompare(String(a)));
  }, [lohnzettel]);

  const openDownload = (url) => {
    window.open(url, '_blank', 'noopener');
  };

  if (loading) {
    return (
      <div className="px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-[var(--bg-inset,#F8F9FC)] rounded w-2/3" />
          <div className="h-32 bg-[var(--bg-inset,#F8F9FC)] rounded-2xl" />
          <div className="h-32 bg-[var(--bg-inset,#F8F9FC)] rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-24">
      <h1 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] mb-4">
        {t('mobilePortal.dokumente.title', 'Dokumente')}
      </h1>

      {/* 1. Offene Vertraege — prominent alert */}
      {offeneVertraege.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-2xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {t('mobilePortal.dokumente.pendingSignature', 'Dienstvertrag wartet auf Unterschrift')}
              </p>
              {offeneVertraege.map(v => (
                <div key={v.id} className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-amber-700 dark:text-amber-300">{v.bezeichnung || t('mobilePortal.dokumente.contract', 'Vertrag')}</span>
                  <StatusBadge status={v.status} t={t} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 2. Lohnzettel */}
      <SectionCard
        title={t('mobilePortal.dokumente.payslips', 'Lohnzettel')}
        icon={FileText}
        count={lohnzettel.length}
        defaultOpen={lohnzettel.length > 0}
      >
        {lohnzettel.length === 0 ? (
          <p className="py-4 text-center text-xs text-[var(--text-tertiary,#6B7280)]">
            {t('mobilePortal.dokumente.noPayslips', 'Keine Lohnzettel vorhanden')}
          </p>
        ) : (
          lohnzettelByYear.map(([year, items]) => (
            <div key={year}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mt-3 mb-1">{year}</p>
              {items.map(lz => (
                <div key={lz.id} className="flex items-center gap-3 py-2.5 border-b border-[var(--border-card,#E5E7EB)]/50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] truncate">
                      {formatMonth(lz.gueltig_ab) || lz.titel || lz.datei_name}
                    </p>
                    <p className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-0.5 flex items-center gap-2">
                      {lz.netto != null ? (
                        <>
                          <span className="font-semibold text-[var(--text-secondary,#4B5563)]">
                            {t('portal.lohnzettel.netto', 'Netto')}: {formatEur(lz.netto)}
                          </span>
                          {lz.brutto != null && (
                            <span>{t('portal.lohnzettel.brutto', 'Brutto')}: {formatEur(lz.brutto)}</span>
                          )}
                        </>
                      ) : (
                        <span>{lz.datei_groesse ? formatSize(lz.datei_groesse) : ''}</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => openDownload(`${API}/api/portal/lohnzettel/${lz.id}/download`)}
                    className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#338edc]/10 flex items-center justify-center active:bg-[#338edc]/20 transition-colors"
                  >
                    <Download className="w-4 h-4 text-[#338edc]" />
                  </button>
                </div>
              ))}
            </div>
          ))
        )}
      </SectionCard>

      {/* 3. Vertraege */}
      <SectionCard
        title={t('mobilePortal.dokumente.contracts', 'Vertraege')}
        icon={Building2}
        count={vertraege.length}
        defaultOpen={vertraege.length > 0}
      >
        {vertraege.length === 0 ? (
          <p className="py-4 text-center text-xs text-[var(--text-tertiary,#6B7280)]">
            {t('mobilePortal.dokumente.noContracts', 'Keine Vertraege vorhanden')}
          </p>
        ) : (
          vertraege.map(v => {
            const isDv = String(v.id).startsWith('dv-');
            const dvId = isDv ? String(v.id).replace('dv-', '') : null;
            const downloadUrl = isDv
              ? `${API}/api/portal/digitale-vertraege/${dvId}/download`
              : `${API}/api/portal/vertraege/${v.id}/download`;
            return (
              <div key={v.id} className="flex items-center gap-3 py-2.5 border-b border-[var(--border-card,#E5E7EB)]/50 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] truncate">
                    {v.bezeichnung || v.vertrag_typ || t('mobilePortal.dokumente.contract', 'Vertrag')}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <StatusBadge status={v.status} t={t} />
                    {v.start_datum && (
                      <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">
                        {isDv ? t('mobilePortal.dokumente.signed', 'Unterschrieben') + ' ' : t('mobilePortal.dokumente.since', 'ab') + ' '}
                        {formatDate(v.start_datum)}
                      </span>
                    )}
                  </div>
                </div>
                {v.pdf_pfad && (
                  <button
                    onClick={() => openDownload(downloadUrl)}
                    className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#338edc]/10 flex items-center justify-center active:bg-[#338edc]/20 transition-colors"
                  >
                    <Download className="w-4 h-4 text-[#338edc]" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </SectionCard>

      {/* 4. Meine Dokumente */}
      <SectionCard
        title={t('mobilePortal.dokumente.myDocs', 'Meine Dokumente')}
        icon={FolderOpen}
        count={meineDoks.length}
        defaultOpen={meineDoks.length > 0}
      >
        {meineDoks.length === 0 ? (
          <p className="py-4 text-center text-xs text-[var(--text-tertiary,#6B7280)]">
            {t('mobilePortal.dokumente.noMyDocs', 'Keine persoenlichen Dokumente')}
          </p>
        ) : (
          meineDoks.map(doc => (
            <div key={doc.id} className="flex items-center gap-3 py-2.5 border-b border-[var(--border-card,#E5E7EB)]/50 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] truncate">{doc.titel || doc.datei_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {doc.kategorie && (
                    <span className="px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[10px] font-medium text-[var(--text-tertiary,#6B7280)]">
                      {doc.kategorie}
                    </span>
                  )}
                  {doc.datei_groesse && (
                    <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{formatSize(doc.datei_groesse)}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => openDownload(`${API}/api/portal/dokumente/${doc.id}/download`)}
                className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#338edc]/10 flex items-center justify-center active:bg-[#338edc]/20 transition-colors"
              >
                <Download className="w-4 h-4 text-[#338edc]" />
              </button>
            </div>
          ))
        )}
      </SectionCard>

      {/* 5. Allgemeine Dokumente */}
      <SectionCard
        title={t('mobilePortal.dokumente.generalDocs', 'Allgemeine Dokumente')}
        icon={Building2}
        count={allgDoks.length}
        defaultOpen={allgDoks.length > 0}
      >
        {allgDoks.length === 0 ? (
          <p className="py-4 text-center text-xs text-[var(--text-tertiary,#6B7280)]">
            {t('mobilePortal.dokumente.noGeneralDocs', 'Keine allgemeinen Dokumente')}
          </p>
        ) : (
          allgDoks.map(doc => (
            <div key={doc.id} className="flex items-center gap-3 py-2.5 border-b border-[var(--border-card,#E5E7EB)]/50 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] truncate">{doc.titel || doc.datei_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {doc.kategorie && (
                    <span className="px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[10px] font-medium text-[var(--text-tertiary,#6B7280)]">
                      {doc.kategorie}
                    </span>
                  )}
                  {doc.beschreibung && (
                    <span className="text-[11px] text-[var(--text-tertiary,#6B7280)] truncate">{doc.beschreibung}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => openDownload(`${API}/api/portal/allgemeine-dokumente/${doc.id}/download`)}
                className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#338edc]/10 flex items-center justify-center active:bg-[#338edc]/20 transition-colors"
              >
                <Download className="w-4 h-4 text-[#338edc]" />
              </button>
            </div>
          ))
        )}
      </SectionCard>
    </div>
  );
}
